import { gql, useQuery } from '@apollo/client';
import {
  defaultSectionVisibility,
  fullPermissionMask,
  getMappedPermissionsForNavSection,
  getNavSectionLabel,
  NavSectionKey,
  navSectionKeys,
  navSectionParent,
  Permissions,
  SectionVisibility,
  sectionVisibilityValues
} from '@app/common/src/permissions';
import { useFlashMutation } from '@everlutionsk/ui-apollo';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  Button as MuiButton,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../components/coreComponents/button';

type OrgPermission = {
  readonly id: string;
  readonly name: string;
  readonly parentId?: string | null;
  readonly isRoot: boolean;
  readonly permissions: ReadonlyArray<{ section: string; value: number }>;
  readonly resolvedPermissions: ReadonlyArray<{ section: string; value: number }>;
  readonly sectionVisibility: ReadonlyArray<{ section: string; visibility: SectionVisibility }>;
  readonly resolvedSectionVisibility: ReadonlyArray<{
    section: string;
    visibility: SectionVisibility;
  }>;
};

type PermissionSectionId = keyof typeof Permissions;

type VisibilityMap = Partial<Record<NavSectionKey, SectionVisibility>>;
type PermissionMap = Partial<Record<PermissionSectionId, number>>;

const visibilityRank: Record<SectionVisibility, number> = {
  hidden: 0,
  marketing: 1,
  enabled: 2
};

const visibilityIcons: Record<SectionVisibility, ReactNode> = {
  hidden: <VisibilityOffOutlinedIcon fontSize="small" />,
  marketing: <CampaignOutlinedIcon fontSize="small" />,
  enabled: <CheckCircleOutlinedIcon fontSize="small" />
};

const visibilityCopy: Record<SectionVisibility, { label: string; tooltip: string }> = {
  hidden: { label: 'Hidden', tooltip: 'Nav link removed; route blocked.' },
  marketing: { label: 'Preview', tooltip: 'Nav link with badge; route shows a marketing page.' },
  enabled: { label: 'Enabled', tooltip: 'Real page renders.' }
};

const visibilityPaletteKey: Record<SectionVisibility, 'error' | 'warning' | 'success'> = {
  hidden: 'error',
  marketing: 'warning',
  enabled: 'success'
};

const navGroups: ReadonlyArray<{
  readonly key: NavSectionKey;
  readonly children: readonly NavSectionKey[];
}> = (() => {
  const groups: Record<string, NavSectionKey[]> = {};
  for (const key of navSectionKeys) {
    const parent = navSectionParent[key];
    if (parent == null) {
      if (groups[key] == null) groups[key] = [];
    } else {
      if (groups[parent] == null) groups[parent] = [];
      groups[parent].push(key);
    }
  }
  return Object.keys(groups).map(parentKey => ({
    key: parentKey as NavSectionKey,
    children: groups[parentKey] as NavSectionKey[]
  }));
})();

function visibilityMapFromList(
  list: ReadonlyArray<{ section: string; visibility: SectionVisibility }>
): VisibilityMap {
  const out: VisibilityMap = {};
  for (const entry of list) out[entry.section as NavSectionKey] = entry.visibility;
  return out;
}

function permissionMapFromList(
  list: ReadonlyArray<{ section: string; value: number }>
): PermissionMap {
  const out: PermissionMap = {};
  for (const entry of list) out[entry.section as PermissionSectionId] = entry.value;
  return out;
}

/**
 * Stable, key-sorted JSON serialiser. Equivalent maps with different
 * key insertion order produce the same string so equality checks
 * (auto-save dirty detection, useEffect deps) don't fire spuriously
 * when the server rebuilds a map in a different order.
 */
function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_k, v) => {
    if (v != null && typeof v === 'object' && !Array.isArray(v)) {
      const sorted: Record<string, unknown> = {};
      for (const key of Object.keys(v).sort()) sorted[key] = (v as Record<string, unknown>)[key];
      return sorted;
    }
    return v;
  });
}

// `parentCap === null` means "no parent" (the org is root) — all
// options stay available. When the org HAS a parent, missing entries
// in the parent's stored map mean the parent has that section at the
// global default (= 'hidden'), so the child can't have it either.
type ParentCap = VisibilityMap | null;

function effectiveCap(section: NavSectionKey, parentCap: ParentCap): SectionVisibility {
  if (parentCap == null) return 'enabled';
  return parentCap[section] ?? defaultSectionVisibility;
}

function allowedVisibilityValues(cap: SectionVisibility): readonly SectionVisibility[] {
  return sectionVisibilityValues.filter(v => visibilityRank[v] <= visibilityRank[cap]);
}

/**
 * Sections that were previously hidden in production (and only shown
 * in dev with a "DEV" badge) before the visibility/permissions system
 * replaced the `environment.isProd` gates. Used by the "Apply
 * production preset" button so an admin can re-create the historical
 * prod nav surface for a customer in one click — these features are
 * still in active development and shouldn't be exposed on prod yet.
 *
 * Refine this list as features ship: removing an entry means
 * "production-ready" → the preset will leave it Enabled.
 */
const productionHiddenSections: readonly NavSectionKey[] = [
  // Governance subsections still in development.
  'governance.complianceCalendar',
  'governance.exceptions',
  // Compliance / framework views still being built.
  'compliance.gapAnalysis',
  'compliance.soa',
  'compliance.assessments',
  'compliance.systemKpis',
  // Incidents group — new flow, not exposed in prod yet.
  'incidents.incidents',
  'incidents.scenarios',
  'incidents.playbooks',
  'incidents.problems',
  // Time tracking group — FE-only so far, backend not yet wired to
  // permissions; keep it off the prod surface until it ships.
  'timetracking.myTimesheet',
  'timetracking.holidays',
  'timetracking.reports',
  'timetracking.exports',
  'timetracking.projects',
  // Training group — new flow.
  'training.videos',
  'training.campaigns',
  // Settings sub-routes still in development.
  'settings.functions',
  'settings.emailTemplates',
  'settings.importExport',
  'settings.accessValidation',
  'settings.invites',
  'settings.sso',
  'settings.billing'
];

/**
 * Set each group-parent's visibility to the max of its children. The
 * parent's value is fully derived — it's the door icon in the nav, and
 * a door without reachable rooms makes no sense, so we don't expose
 * an independent parent toggle. Groups without children (e.g. the
 * 'overview' leaf) are left as-is.
 */
function deriveParentVisibility(visibility: VisibilityMap): VisibilityMap {
  const next: VisibilityMap = { ...visibility };
  for (const group of navGroups) {
    if (group.children.length === 0) continue;
    let maxValue: SectionVisibility = 'hidden';
    for (const child of group.children) {
      const childValue = next[child] ?? defaultSectionVisibility;
      if (visibilityRank[childValue] > visibilityRank[maxValue]) {
        maxValue = childValue;
      }
    }
    next[group.key] = maxValue;
  }
  return next;
}

/**
 * Compute the ACL bitmap an org should hold given its visibility map
 * and per-permission overrides.
 *
 *   1. Collect every permission section unlocked by an Enabled nav
 *      section (a "granted perm").
 *   2. For each granted perm: if `aclOverrides[perm]` exists, use it.
 *      Otherwise default to `fullPermissionMask`.
 *
 * Overrides are stored per PERMISSION SECTION, not per nav section.
 * Multiple nav sections may grant the same permission (e.g. both
 * `organisation.businessServices` and `organisation.assets` grant
 * `asset`) — the override is the single source of truth, so capping
 * a permission below full takes effect regardless of how many nav
 * sections happen to grant it.
 */
function computeAclFromVisibility({
  visibility,
  aclOverrides
}: {
  visibility: VisibilityMap;
  aclOverrides: PermissionMap;
}): PermissionMap {
  const acl: PermissionMap = {};
  for (const section of navSectionKeys) {
    if (visibility[section] !== 'enabled') continue;
    const mapped = getMappedPermissionsForNavSection(section);
    if (mapped.length === 0) continue;
    for (const perm of mapped) {
      const bits = aclOverrides[perm] ?? fullPermissionMask;
      acl[perm] = bits;
    }
  }
  return acl;
}

export function AccountPermissionsTab({ accountId }: { accountId: string }) {
  const { data, loading, error } = useQuery(accountOrganisationPermissionsQuery, {
    variables: { accountId },
    fetchPolicy: 'cache-and-network'
  });

  // Tenant-wide production preset: applies the "production-safe"
  // recipe (every section enabled except the dev-only ones in
  // `productionHiddenSections`) to EVERY org in the account, in
  // root → leaf order so the waterfall sees the right parent state
  // on each save. Replaces the per-org button — admins only need
  // one click to make a fresh tenant prod-safe.
  const [save, { loading: presetSaving }] = useFlashMutation(updateMutation, {
    refetchQueries: ['AccountOrganisationPermissions'],
    successMsg: 'Applied production preset to every organisation.'
  });

  if (loading && data == null) return <Typography color="text.secondary">Loading…</Typography>;
  if (error != null) {
    return <Alert severity="error">Failed to load permissions: {error.message}</Alert>;
  }

  const snapshot = data?.accountOrganisationPermissions;
  if (snapshot == null)
    return <Typography color="text.secondary">No organisations found.</Typography>;

  const sorted = [...snapshot.organisations].sort((a, b) => {
    if (a.isRoot !== b.isRoot) return a.isRoot ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  const applyProductionPresetToTenant = async () => {
    const devOnly = new Set<NavSectionKey>(productionHiddenSections);

    // Compute the visibility + ACL the preset wants for any org
    // (independent of waterfall — the server clamps each save).
    const presetVisibility: VisibilityMap = {};
    for (const group of navGroups) {
      const targets = group.children.length === 0 ? [group.key] : group.children;
      for (const section of targets) {
        presetVisibility[section] = devOnly.has(section) ? 'hidden' : 'enabled';
      }
    }
    const derivedPresetVisibility = deriveParentVisibility(presetVisibility);
    const presetAcl = computeAclFromVisibility({
      visibility: derivedPresetVisibility,
      aclOverrides: {}
    });

    const sectionVisibilityEntries = Object.entries(derivedPresetVisibility).map(
      ([section, visibility]) => ({ section, visibility: visibility as SectionVisibility })
    );
    const permissionEntries = Object.entries(presetAcl).map(([section, value]) => ({
      section,
      value: value as number
    }));

    // Walk root → leaf so each child's save sees the parent already
    // updated server-side, otherwise `clampAclByAncestors` /
    // `applySectionVisibility` would clamp the child down against a
    // stale parent.
    for (const org of sorted) {
      try {
        await save({
          variables: {
            input: {
              accountId,
              organisationId: org.id,
              sectionVisibility: sectionVisibilityEntries,
              permissions: permissionEntries
            }
          }
        });
      } catch {
        // useFlashMutation surfaced the error; keep going so partial
        // failures don't block the rest of the tenant.
      }
    }
  };

  // Per-org cap from the immediate parent. Root: empty (all options allowed).
  // Child of root: root's STORED visibility (not resolved — root.resolved is
  // whitelist-clamped for root's own UI and that whitelist doesn't propagate).
  // Grandchild+: parent's resolvedSectionVisibility (= raw waterfall for
  // non-root parents).
  const orgById = new Map(snapshot.organisations.map(o => [o.id, o]));
  const parentCapByOrgId = new Map<string, ParentCap>();
  const parentNameByOrgId = new Map<string, string | null>();
  for (const org of snapshot.organisations) {
    const parent = org.parentId == null ? null : (orgById.get(org.parentId) ?? null);
    parentNameByOrgId.set(org.id, parent?.name ?? null);
    if (parent == null) {
      // No parent (root) → null cap. effectiveCap treats this as "no
      // restriction" so every toggle stays available.
      parentCapByOrgId.set(org.id, null);
      continue;
    }
    const cap = parent.isRoot
      ? visibilityMapFromList(parent.sectionVisibility)
      : visibilityMapFromList(parent.resolvedSectionVisibility);
    parentCapByOrgId.set(org.id, cap);
  }

  return (
    <Stack spacing={4}>
      <Card
        variant="outlined"
        sx={theme => ({
          borderRadius: 2,
          borderWidth: 2,
          borderColor: theme.palette.primary.main,
          bgcolor: alpha(theme.palette.primary.main, 0.04)
        })}
      >
        <CardContent sx={{ py: 3, px: 3, '&:last-child': { pb: 3 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={2.5}
          >
            <Stack spacing={2} direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 700, mb: 0.5 }}>
                Production preset
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Applies the production-safe recipe to <strong>every organisation</strong> in this
                tenant: enables every section that ships to production, hides the ones still in
                development. Use it after creating a new tenant.
              </Typography>
            </Stack>
            <Tooltip title="Apply to every organisation in this tenant, root → leaf, so the waterfall lands clean.">
              <span>
                <MuiButton
                  size="medium"
                  variant="contained"
                  color="primary"
                  disableElevation
                  disabled={presetSaving}
                  onClick={applyProductionPresetToTenant}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 999,
                    px: 3,
                    py: 1
                  }}
                >
                  {presetSaving ? 'Applying…' : 'Apply production preset'}
                </MuiButton>
              </span>
            </Tooltip>
          </Stack>
        </CardContent>
      </Card>

      {sorted.map(org => (
        <OrganisationCard
          key={org.id}
          accountId={accountId}
          org={org}
          parentCap={parentCapByOrgId.get(org.id) ?? null}
          parentName={parentNameByOrgId.get(org.id) ?? null}
        />
      ))}
    </Stack>
  );
}

function OrganisationCard({
  accountId,
  org,
  parentCap,
  parentName
}: {
  accountId: string;
  org: OrgPermission;
  parentCap: ParentCap;
  parentName: string | null;
}) {
  const initialVisibility = useMemo(
    () => deriveParentVisibility(visibilityMapFromList(org.sectionVisibility)),
    [org]
  );
  const initialPermissions = useMemo(() => permissionMapFromList(org.permissions), [org]);

  // Reverse-engineer global per-permission overrides from the stored
  // ACL. For every permission section that's granted by at least one
  // Enabled nav section, if the stored bits aren't full record an
  // override. Overrides are keyed by Permission section (not nav
  // section) so they survive multiple nav sections granting the same
  // perm.
  const initialAclOverrides = useMemo<PermissionMap>(() => {
    const overrides: PermissionMap = {};
    const grantedPerms = new Set<PermissionSectionId>();
    for (const section of navSectionKeys) {
      if (initialVisibility[section] !== 'enabled') continue;
      for (const perm of getMappedPermissionsForNavSection(section)) {
        grantedPerms.add(perm);
      }
    }
    for (const perm of grantedPerms) {
      const stored = initialPermissions[perm] ?? 0;
      if (stored !== fullPermissionMask) overrides[perm] = stored;
    }
    return overrides;
  }, [initialVisibility, initialPermissions]);

  const [visibilityDraft, setVisibilityDraft] = useState<VisibilityMap>(initialVisibility);
  const [aclOverrides, setAclOverrides] = useState<PermissionMap>(initialAclOverrides);

  // useMutation directly (not useFlashMutation) so we can drive the
  // status indicator inline — the manual Save button is gone, so a
  // flash toast on every keystroke would be noise. Errors still flash
  // via the catch block below, surfaced through the shared flash hub.
  const [save] = useFlashMutation(updateMutation, {
    refetchQueries: ['AccountOrganisationPermissions'],
    successMsg: 'Permissions updated.'
  });

  type SaveStatus =
    | { state: 'idle' }
    | { state: 'pending' }
    | { state: 'saving' }
    | { state: 'saved'; at: number }
    | { state: 'failed' };
  const [saveStatus, setSaveStatus] = useState<SaveStatus>({ state: 'idle' });

  // Resync local draft state when the server snapshot for this org
  // changes. This is how the child accordion picks up the cascade after
  // an ancestor (root) save tightens the child's stored permissions /
  // visibility via the server's `propagateAclClamp` /
  // `propagateVisibilityClamp` passes. Without this, the child UI sits
  // on stale state forever.
  //
  // Skipped while OUR OWN save is queued or in flight, otherwise we
  // race the user's edits and clobber them.
  const saveStatusRef = useRef(saveStatus);
  saveStatusRef.current = saveStatus;
  const incomingKey = useMemo(
    () => stableStringify({ v: initialVisibility, a: initialAclOverrides }),
    [initialVisibility, initialAclOverrides]
  );
  useEffect(() => {
    if (saveStatusRef.current.state === 'pending' || saveStatusRef.current.state === 'saving') {
      return;
    }
    setVisibilityDraft(initialVisibility);
    setAclOverrides(initialAclOverrides);
  }, [incomingKey]);

  // Snapshot the latest visibility + override drafts in refs so the
  // debounce timer always closes over the freshest state without needing
  // to retrigger the effect on every keystroke.
  // Stash everything the debounced save needs in refs so the effect's
  // dep array can be JUST the serialised drafts. `save` from
  // useFlashMutation is not stable across renders, and including it (or
  // any of the unstable props) in the deps would re-run the effect on
  // every render → setSaveStatus → re-render → loop.
  const visibilityDraftRef = useRef(visibilityDraft);
  const aclOverridesRef = useRef(aclOverrides);
  const saveRef = useRef(save);
  const orgRef = useRef({ id: org.id, isRoot: org.isRoot, accountId });
  visibilityDraftRef.current = visibilityDraft;
  aclOverridesRef.current = aclOverrides;
  saveRef.current = save;
  orgRef.current = { id: org.id, isRoot: org.isRoot, accountId };

  // Serialised drafts as a primitive key — sorted so identical maps
  // produce identical strings regardless of key insertion order. The
  // draft is built by the user (insertion order = click order) while
  // `initialVisibility` is rebuilt from the server's array (server
  // order), so a plain `JSON.stringify` would say "different" even
  // when the maps are semantically equal — and the auto-save effect
  // would re-fire after every refetch, causing the double mutation.
  const draftKey = stableStringify({ v: visibilityDraft, a: aclOverrides });
  const initialKey = useMemo(
    () => stableStringify({ v: initialVisibility, a: initialAclOverrides }),
    [initialVisibility, initialAclOverrides]
  );

  useEffect(() => {
    if (draftKey === initialKey) {
      setSaveStatus({ state: 'idle' });
      return;
    }

    setSaveStatus({ state: 'pending' });
    const timer = window.setTimeout(async () => {
      setSaveStatus({ state: 'saving' });
      try {
        const acl = computeAclFromVisibility({
          visibility: visibilityDraftRef.current,
          aclOverrides: aclOverridesRef.current
        });
        await saveRef.current({
          variables: {
            input: {
              accountId: orgRef.current.accountId,
              organisationId: orgRef.current.id,
              sectionVisibility: Object.entries(visibilityDraftRef.current).map(
                ([section, visibility]) => ({
                  section,
                  visibility: visibility as SectionVisibility
                })
              ),
              permissions: Object.entries(acl).map(([section, value]) => ({
                section,
                value: value as number
              }))
            }
          }
        });
        setSaveStatus({ state: 'saved', at: Date.now() });
      } catch {
        // useFlashMutation surfaced the toast; flag the indicator too.
        setSaveStatus({ state: 'failed' });
      }
    }, 600);

    return () => window.clearTimeout(timer);
  }, [draftKey, initialKey]);

  return (
    <Accordion
      defaultExpanded={false}
      disableGutters
      sx={{ borderRadius: 2, '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ flex: 1, flexWrap: 'wrap', rowGap: 0.5 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            {org.isRoot ? (
              <AccountTreeOutlinedIcon
                fontSize="small"
                sx={theme => ({ color: theme.palette.warning.main })}
              />
            ) : (
              <BusinessOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            )}
            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600 }}>
              {org.name}
            </Typography>
          </Stack>

          {org.isRoot ? (
            <Chip
              size="small"
              label="Tenant root"
              color="warning"
              icon={<AccountTreeOutlinedIcon fontSize="small" />}
              sx={{
                // Admin theme sets `min-width: 200px` and
                // `justify-content: space-between` on every chip, which
                // flings the icon and label to opposite ends. Override
                // locally so the chip is content-sized.
                width: 'fit-content',
                minWidth: 0,
                justifyContent: 'flex-start',
                flexShrink: 0,
                alignSelf: 'center',
                // Admin theme strips label padding to 0 and MUI's
                // default chip icon has margin-right: -6px — together
                // they collapse the icon hard against the label.
                // Force a real gap.
                '& .MuiChip-icon': { ml: 0, mr: 1 }
              }}
            />
          ) : parentName != null ? (
            <Tooltip title={`Child organisation of ${parentName}`}>
              <Chip
                size="small"
                variant="outlined"
                icon={<ApartmentOutlinedIcon fontSize="small" />}
                label={
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="labelSmall" color="text.secondary">
                      Child of
                    </Typography>
                    <Typography
                      variant="labelSmall"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      {parentName}
                    </Typography>
                  </Stack>
                }
                sx={{
                  color: 'text.secondary',
                  width: 'fit-content',
                  minWidth: 0,
                  justifyContent: 'flex-start',
                  flexShrink: 0,
                  alignSelf: 'center',
                  '& .MuiChip-icon': { ml: 0, mr: 1 }
                }}
              />
            </Tooltip>
          ) : null}

          <SaveStatusChip status={saveStatus} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={3}>
          <VisibilityPanel
            parentCap={parentCap}
            visibilityDraft={visibilityDraft}
            setVisibilityDraft={setVisibilityDraft}
            aclOverrides={aclOverrides}
            setAclOverrides={setAclOverrides}
            isRoot={org.isRoot}
            disabled={saveStatus.state === 'saving'}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function SaveStatusChip({
  status
}: {
  status:
    | { state: 'idle' }
    | { state: 'pending' }
    | { state: 'saving' }
    | { state: 'saved'; at: number }
    | { state: 'failed' };
}) {
  // After 'saved' we let the chip linger briefly then fade to idle so
  // the admin can see the change committed without it becoming
  // permanent visual noise.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (status.state !== 'saved') return;
    const t = window.setTimeout(() => setNow(Date.now()), 4000);
    return () => window.clearTimeout(t);
  }, [status]);

  if (status.state === 'idle') return null;
  if (status.state === 'pending') {
    return (
      <Chip size="small" label="Pending…" variant="outlined" sx={{ color: 'text.secondary' }} />
    );
  }
  if (status.state === 'saving') {
    return (
      <Chip
        size="small"
        label="Saving…"
        color="info"
        variant="outlined"
        sx={{ color: 'text.secondary' }}
      />
    );
  }
  if (status.state === 'failed') {
    return <Chip size="small" label="Save failed" color="error" variant="outlined" />;
  }
  // saved
  if (now - status.at > 4000) return null;
  return (
    <Chip
      size="small"
      label="Saved"
      color="success"
      variant="outlined"
      sx={{ color: 'text.secondary' }}
    />
  );
}

function VisibilityPanel({
  parentCap,
  visibilityDraft,
  setVisibilityDraft,
  aclOverrides,
  setAclOverrides,
  isRoot,
  disabled
}: {
  parentCap: ParentCap;
  visibilityDraft: VisibilityMap;
  setVisibilityDraft: React.Dispatch<React.SetStateAction<VisibilityMap>>;
  aclOverrides: PermissionMap;
  setAclOverrides: React.Dispatch<React.SetStateAction<PermissionMap>>;
  isRoot: boolean;
  disabled: boolean;
}) {
  const visibleGroups = navGroups.filter(group => effectiveCap(group.key, parentCap) !== 'hidden');

  if (visibleGroups.length === 0) {
    return (
      <Alert severity="warning">
        The parent organisation has every section hidden — nothing can be enabled here until the
        parent opens something up.
      </Alert>
    );
  }

  // When the admin flips a section's visibility off (Hidden or Preview), drop
  // overrides for any permissions that are no longer granted by ANY enabled
  // section — those bits do nothing while the perm is unreachable.
  const dropOrphanedOverrides = (visibility: VisibilityMap) => {
    setAclOverrides(prev => {
      const stillGranted = new Set<PermissionSectionId>();
      for (const section of navSectionKeys) {
        if (visibility[section] !== 'enabled') continue;
        for (const perm of getMappedPermissionsForNavSection(section)) {
          stillGranted.add(perm);
        }
      }
      const next: PermissionMap = {};
      let changed = false;
      for (const perm of Object.keys(prev) as PermissionSectionId[]) {
        if (stillGranted.has(perm)) {
          next[perm] = prev[perm]!;
        } else {
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  };

  const setVisibilityFor = (section: NavSectionKey, value: SectionVisibility) => {
    setVisibilityDraft(prev => {
      const next = deriveParentVisibility({ ...prev, [section]: value });
      dropOrphanedOverrides(next);
      return next;
    });
  };

  const setGroup = (
    group: NavSectionKey,
    children: readonly NavSectionKey[],
    value: SectionVisibility
  ) => {
    setVisibilityDraft(prev => {
      const next: VisibilityMap = { ...prev };
      if (children.length === 0) {
        // Leaf group (e.g. 'overview') has no children to cascade to —
        // set the group key directly. The derivation step otherwise
        // only touches parents with children and would leave this
        // untouched.
        next[group] = clampToCap(value, effectiveCap(group, parentCap));
      } else {
        for (const child of children) {
          next[child] = clampToCap(value, effectiveCap(child, parentCap));
        }
      }
      const derived = deriveParentVisibility(next);
      dropOrphanedOverrides(derived);
      return derived;
    });
  };

  // The org-wide bulk should only offer values that at least ONE
  // visible section could actually take given the parent's waterfall
  // cap. If every visible row is capped at 'marketing', "Enable
  // everything" would silently clamp to 'marketing' — better to not
  // even show the button. The highest cap across all visible rows
  // defines the ceiling we expose.
  const maxOrgWideCap = (() => {
    let rank = 0;
    for (const group of visibleGroups) {
      const targets = group.children.length === 0 ? [group.key] : group.children;
      for (const section of targets) {
        const cap = effectiveCap(section, parentCap);
        if (visibilityRank[cap] > rank) rank = visibilityRank[cap];
      }
    }
    return sectionVisibilityValues.find(v => visibilityRank[v] === rank) ?? 'hidden';
  })();
  const orgWideValues = sectionVisibilityValues.filter(
    v => visibilityRank[v] <= visibilityRank[maxOrgWideCap]
  );

  // Org-wide bulk: set every visible row across every visible group
  // to the same value in one click. Useful for provisioning new
  // tenants ("enable everything the customer paid for") or shutting
  // down a misbehaving tenant ("hide everything").
  const setAllSections = (value: SectionVisibility) => {
    setVisibilityDraft(prev => {
      const next: VisibilityMap = { ...prev };
      for (const group of visibleGroups) {
        if (group.children.length === 0) {
          next[group.key] = clampToCap(value, effectiveCap(group.key, parentCap));
        } else {
          for (const child of group.children) {
            if (effectiveCap(child, parentCap) === 'hidden') continue;
            next[child] = clampToCap(value, effectiveCap(child, parentCap));
          }
        }
      }
      const derived = deriveParentVisibility(next);
      dropOrphanedOverrides(derived);
      return derived;
    });
  };

  return (
    <Stack spacing={2}>
      <Card
        variant="outlined"
        sx={theme => ({
          borderRadius: 2,
          // Distinct emphasis so this row reads as the master control,
          // not "just another group card".
          borderWidth: 2,
          borderColor: theme.palette.primary.main,
          bgcolor: alpha(theme.palette.primary.main, 0.04)
        })}
      >
        <CardContent sx={{ py: 3, px: 3, '&:last-child': { pb: 3 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={2.5}
          >
            <Box>
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 700, mb: 0.5 }}>
                Apply to every section
              </Typography>
              <Typography variant="body2" color="text.secondary">
                One-click bulk for this organisation — overrides every visible row in every group
                below.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5}>
              {orgWideValues.map(value => {
                const paletteKey = visibilityPaletteKey[value];
                return (
                  <Tooltip
                    key={value}
                    title={`Set EVERY section across this organisation to "${visibilityCopy[value].label}"`}
                  >
                    <span>
                      <MuiButton
                        size="medium"
                        variant="contained"
                        color={paletteKey}
                        disableElevation
                        disabled={disabled}
                        onClick={() => setAllSections(value)}
                        startIcon={visibilityIcons[value]}
                        sx={theme => {
                          const { main } = theme.palette[paletteKey];
                          const dark = theme.palette[paletteKey].dark ?? main;
                          return {
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: 999,
                            px: 3,
                            py: 1,
                            bgcolor: main,
                            color: theme.palette.getContrastText(main),
                            '& .MuiButton-startIcon': { mr: 1, ml: 0 },
                            '&:hover': {
                              bgcolor: dark,
                              color: theme.palette.getContrastText(dark)
                            },
                            '&.Mui-disabled': {
                              bgcolor: theme.palette.action.disabledBackground,
                              color: theme.palette.text.disabled
                            }
                          };
                        }}
                      >
                        {visibilityCopy[value].label} everything
                      </MuiButton>
                    </span>
                  </Tooltip>
                );
              })}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {visibleGroups.map(group => (
        <VisibilityGroupCard
          key={group.key}
          group={group}
          parentCap={parentCap}
          visibilityDraft={visibilityDraft}
          aclOverrides={aclOverrides}
          setAclOverrides={setAclOverrides}
          onChange={setVisibilityFor}
          onBulkSet={value => setGroup(group.key, group.children, value)}
          isRoot={isRoot}
          disabled={disabled}
        />
      ))}
    </Stack>
  );
}

function clampToCap(value: SectionVisibility, cap: SectionVisibility): SectionVisibility {
  return visibilityRank[value] <= visibilityRank[cap] ? value : cap;
}

function VisibilityGroupCard({
  group,
  parentCap,
  visibilityDraft,
  aclOverrides,
  setAclOverrides,
  onChange,
  onBulkSet,
  isRoot,
  disabled
}: {
  group: { readonly key: NavSectionKey; readonly children: readonly NavSectionKey[] };
  parentCap: ParentCap;
  visibilityDraft: VisibilityMap;
  aclOverrides: PermissionMap;
  setAclOverrides: React.Dispatch<React.SetStateAction<PermissionMap>>;
  onChange: (section: NavSectionKey, value: SectionVisibility) => void;
  onBulkSet: (value: SectionVisibility) => void;
  isRoot: boolean;
  disabled: boolean;
}) {
  const groupCap = effectiveCap(group.key, parentCap);
  const visibleChildren = group.children.filter(c => effectiveCap(c, parentCap) !== 'hidden');

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: visibleChildren.length === 0 ? 0 : 2 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
              {getNavSectionLabel(group.key)}
            </Typography>
            {/* Show the parent cap only when it's actually restrictive. */}
            {/* 'enabled' = no cap (or no parent); 'hidden' would have already */}
            {/* dropped the whole group. So this surfaces only for 'marketing'. */}
            {groupCap === 'marketing' && (
              <Tooltip title="The parent organisation caps this group to Preview — Enabled is not available here.">
                <Chip
                  size="small"
                  variant="outlined"
                  color="warning"
                  label="Parent caps to Preview"
                  sx={{ color: 'text.secondary' }}
                />
              </Tooltip>
            )}
          </Stack>

          <Stack direction="row" spacing={1}>
            {allowedVisibilityValues(groupCap).map(value => {
              const paletteKey = visibilityPaletteKey[value];
              return (
                <Tooltip
                  key={value}
                  title={`Apply "${visibilityCopy[value].label}" to the whole group`}
                >
                  <span>
                    <MuiButton
                      size="small"
                      variant="contained"
                      color={paletteKey}
                      disableElevation
                      disabled={disabled}
                      onClick={() => onBulkSet(value)}
                      startIcon={visibilityIcons[value]}
                      sx={theme => {
                        const { main } = theme.palette[paletteKey];
                        const dark = theme.palette[paletteKey].dark ?? main;
                        return {
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: 999,
                          // Wider horizontal padding so "all" never hugs
                          // the right edge of the pill. The icon also
                          // gets explicit right-margin (default MUI
                          // startIcon mr is -2 + 8, which is too tight
                          // once the admin theme overrides kick in).
                          px: 2.5,
                          bgcolor: main,
                          color: theme.palette.getContrastText(main),
                          '& .MuiButton-startIcon': { mr: 1, ml: 0 },
                          '&:hover': {
                            bgcolor: dark,
                            color: theme.palette.getContrastText(dark)
                          },
                          '&.Mui-disabled': {
                            bgcolor: theme.palette.action.disabledBackground,
                            color: theme.palette.text.disabled
                          }
                        };
                      }}
                    >
                      {visibilityCopy[value].label} all
                    </MuiButton>
                  </span>
                </Tooltip>
              );
            })}
          </Stack>
        </Stack>

        {/* Parent-group rows are intentionally NOT toggled directly.
            The parent's visibility is derived from the max of its
            children at save time — a group "door" with no reachable
            rooms inside makes no sense, and an independent parent
            toggle was the source of confusing "empty door" states.
            Leaf groups (no children, e.g. 'overview') still render a
            single direct toggle. */}
        {group.children.length === 0 ? (
          <VisibilityRow
            section={group.key}
            isParent
            cap={groupCap}
            value={visibilityDraft[group.key] ?? defaultSectionVisibility}
            aclOverrides={aclOverrides}
            setAclOverrides={setAclOverrides}
            onChange={v => onChange(group.key, v)}
            disabled={disabled}
          />
        ) : (
          visibleChildren.length > 0 && (
            <Stack spacing={0.5} sx={{ pl: 2, borderLeft: 2, borderColor: 'divider' }}>
              {visibleChildren.map(childKey => (
                <VisibilityRow
                  key={childKey}
                  section={childKey}
                  isParent={false}
                  cap={effectiveCap(childKey, parentCap)}
                  value={visibilityDraft[childKey] ?? defaultSectionVisibility}
                  aclOverrides={aclOverrides}
                  setAclOverrides={setAclOverrides}
                  onChange={v => onChange(childKey, v)}
                  disabled={disabled}
                />
              ))}
            </Stack>
          )
        )}
      </CardContent>
    </Card>
  );
}

function VisibilityRow({
  section,
  isParent,
  cap,
  value,
  aclOverrides,
  setAclOverrides,
  onChange,
  disabled
}: {
  section: NavSectionKey;
  isParent: boolean;
  cap: SectionVisibility;
  value: SectionVisibility;
  aclOverrides: PermissionMap;
  setAclOverrides: React.Dispatch<React.SetStateAction<PermissionMap>>;
  onChange: (value: SectionVisibility) => void;
  disabled: boolean;
}) {
  const allowed = allowedVisibilityValues(cap);
  const displayValue = visibilityRank[value] > visibilityRank[cap] ? cap : value;

  const mappedPerms = getMappedPermissionsForNavSection(section);
  const canCustomise = value === 'enabled' && mappedPerms.length > 0;
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const showAdvanced = canCustomise && advancedOpen;
  // The gear glows red if ANY perm mapped to this section has a stored
  // override (i.e. bits below full). Overrides are now global per perm,
  // so the same override may be reflected in multiple rows that grant
  // it — that's correct.
  const hasCustomCap = mappedPerms.some(perm => aclOverrides[perm] !== undefined);

  return (
    <Stack
      direction="column"
      spacing={1}
      sx={theme => ({
        py: 1,
        // Subtle divider between rows so adjacent items don't blur
        // together — especially important once an Advanced panel
        // opens up beneath one of them.
        borderTop: 1,
        borderColor: theme.palette.divider,
        // When the Advanced editor is open, give the whole block a
        // light red-tinted background + matching border so it's
        // obvious which row owns the expanded panel.
        ...(showAdvanced && {
          mx: -1,
          px: 1,
          borderRadius: 1,
          borderLeft: 3,
          borderLeftColor: theme.palette.error.main,
          bgcolor: alpha(theme.palette.error.main, 0.04)
        })
      })}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Box sx={{ minWidth: 260, flex: 1 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: isParent ? 600 : 500 }}
          >
            {getNavSectionLabel(section)}
          </Typography>
          <Typography variant="labelSmall" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {section}
          </Typography>
        </Box>

        {/* Fixed-width slot for the Advanced gear — kept reserved even
            when the row has no mapped perms so the toggle group below
            sits at the same horizontal position across every row. */}
        <Box
          sx={{
            width: 40,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexShrink: 0
          }}
        >
          {canCustomise && (
            <Tooltip
              title={
                hasCustomCap
                  ? 'Custom ACL applied — click to show actions'
                  : 'Customise which actions are granted for this section'
              }
            >
              <IconButton
                size="small"
                disabled={disabled}
                onClick={() => setAdvancedOpen(o => !o)}
                sx={theme => ({
                  // Red signals "some bits are off" — clearer than the
                  // yellow it used previously, because yellow reads as
                  // "preview / warning" elsewhere in this UI.
                  color: hasCustomCap ? theme.palette.error.main : 'text.secondary',
                  border: 1,
                  borderColor: hasCustomCap
                    ? alpha(theme.palette.error.main, 0.5)
                    : theme.palette.divider,
                  bgcolor: hasCustomCap ? alpha(theme.palette.error.main, 0.08) : 'transparent',
                  borderRadius: 1,
                  '&:hover': hasCustomCap
                    ? { bgcolor: alpha(theme.palette.error.main, 0.16) }
                    : undefined,
                  '&.Mui-disabled': {
                    color: theme.palette.text.disabled,
                    borderColor: theme.palette.action.disabledBackground,
                    bgcolor: alpha(theme.palette.action.disabled, 0.3),
                    cursor: 'not-allowed'
                  }
                })}
              >
                <TuneOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <ToggleButtonGroup
          size="small"
          exclusive
          disabled={disabled}
          value={displayValue}
          onChange={(_, next) => {
            if (next == null) return;
            onChange(next as SectionVisibility);
          }}
          sx={{ minHeight: 36 }}
        >
          {allowed.map(v => {
            const paletteKey = visibilityPaletteKey[v];
            return (
              <ToggleButton
                key={v}
                value={v}
                sx={theme => {
                  const { main } = theme.palette[paletteKey];
                  const dark = theme.palette[paletteKey].dark ?? main;
                  return {
                    textTransform: 'none',
                    px: 2,
                    fontWeight: 500,
                    // Unselected = neutral ghost so the selected pill
                    // visually pops. The icon retains its semantic
                    // colour as a hint of what each option does.
                    color: theme.palette.text.secondary,
                    bgcolor: 'transparent',
                    borderColor: theme.palette.divider,
                    '& .MuiSvgIcon-root': { color: main },
                    '&:hover': {
                      bgcolor: alpha(main, 0.06),
                      borderColor: alpha(main, 0.5)
                    },
                    // Selected = bold solid colour fill, contrasting text.
                    '&.Mui-selected': {
                      bgcolor: main,
                      color: theme.palette.getContrastText(main),
                      borderColor: main,
                      fontWeight: 700,
                      '& .MuiSvgIcon-root': {
                        color: theme.palette.getContrastText(main)
                      },
                      '&:hover': {
                        bgcolor: dark,
                        color: theme.palette.getContrastText(dark),
                        borderColor: dark,
                        '& .MuiSvgIcon-root': {
                          color: theme.palette.getContrastText(dark)
                        }
                      }
                    },
                    // Disabled = washed out, no icon colour either.
                    '&.Mui-disabled': {
                      color: theme.palette.text.disabled,
                      borderColor: theme.palette.action.disabledBackground,
                      bgcolor: 'transparent',
                      cursor: 'not-allowed',
                      fontWeight: 500,
                      '& .MuiSvgIcon-root': { color: theme.palette.text.disabled }
                    },
                    '&.Mui-disabled.Mui-selected': {
                      color: theme.palette.text.disabled,
                      borderColor: theme.palette.action.disabledBackground,
                      bgcolor: alpha(theme.palette.action.disabled, 0.3),
                      fontWeight: 500,
                      '& .MuiSvgIcon-root': { color: theme.palette.text.disabled }
                    }
                  };
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  {visibilityIcons[v]}
                  <span>{visibilityCopy[v].label}</span>
                </Stack>
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>

        {visibilityRank[value] > visibilityRank[cap] && (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={`stored ${value} → capped to ${cap}`}
            sx={{ color: 'text.secondary' }}
          />
        )}
      </Stack>

      {canCustomise && (
        <Collapse in={showAdvanced} unmountOnExit>
          <AclOverrideEditor
            mappedPerms={mappedPerms}
            aclOverrides={aclOverrides}
            setAclOverrides={setAclOverrides}
            disabled={disabled}
          />
        </Collapse>
      )}
    </Stack>
  );
}

function AclOverrideEditor({
  mappedPerms,
  aclOverrides,
  setAclOverrides,
  disabled
}: {
  mappedPerms: PermissionSectionId[];
  aclOverrides: PermissionMap;
  setAclOverrides: React.Dispatch<React.SetStateAction<PermissionMap>>;
  disabled: boolean;
}) {
  // Toggling a switch sets bits on the GLOBAL per-permission map. If the
  // bits land back on `fullPermissionMask` the entry is removed so we
  // collapse to "no override; default auto-grant applies."
  const setBits = (perm: PermissionSectionId, bits: number) => {
    setAclOverrides(prev => {
      const next = { ...prev };
      if (bits === fullPermissionMask) delete next[perm];
      else next[perm] = bits;
      return next;
    });
  };

  const resetAll = () => {
    setAclOverrides(prev => {
      // Only drop entries for perms this row controls — other rows'
      // overrides stay intact.
      const next = { ...prev };
      for (const perm of mappedPerms) delete next[perm];
      return next;
    });
  };

  return (
    <Box
      sx={theme => ({
        mt: 1,
        ml: { xs: 0, sm: '260px' },
        p: 2,
        borderRadius: 1,
        bgcolor: alpha(theme.palette.error.main, 0.06),
        border: 1,
        borderColor: alpha(theme.palette.error.main, 0.25)
      })}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="labelSmall" color="text.secondary" sx={{ fontWeight: 600 }}>
          Action overrides — uncheck to remove specific bits while keeping the section enabled
        </Typography>
        <Button variant="secondary" size="sm" disabled={disabled} onClick={resetAll}>
          Reset to full
        </Button>
      </Stack>

      <Stack spacing={1.5}>
        {mappedPerms.map(perm => {
          const actions = Object.keys(Permissions[perm]) as string[];
          const bits = aclOverrides[perm] ?? fullPermissionMask;
          return (
            <Box key={perm}>
              <Typography
                variant="labelSmall"
                color="text.secondary"
                sx={{ fontFamily: 'monospace', fontWeight: 600, mb: 0.5 }}
              >
                {perm}
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                {actions.map(action => {
                  const mask = (Permissions[perm] as Record<string, number>)[action];
                  const enabled = (bits & mask) === mask;
                  return (
                    <FormControlLabel
                      key={action}
                      control={
                        <Switch
                          size="small"
                          checked={enabled}
                          disabled={disabled}
                          onChange={e => {
                            const nextBits = e.target.checked ? bits | mask : bits & ~mask;
                            setBits(perm, nextBits);
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.secondary">
                          {action}
                        </Typography>
                      }
                    />
                  );
                })}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

const accountOrganisationPermissionsQuery = gql<AccountOrganisationPermissionsGQL>`
  query AccountOrganisationPermissions($accountId: ID!) {
    accountOrganisationPermissions(accountId: $accountId) {
      accountId
      organisations {
        id
        name
        parentId
        isRoot
        permissions {
          section
          value
        }
        resolvedPermissions {
          section
          value
        }
        sectionVisibility {
          section
          visibility
        }
        resolvedSectionVisibility {
          section
          visibility
        }
      }
    }
  }
`;

const updateMutation = gql<AccountOrganisationPermissionsUpdateGQL>`
  mutation AccountOrganisationPermissionsUpdate(
    $input: AccountOrganisationPermissionsUpdateInput!
  ) {
    accountOrganisationPermissionsUpdate(input: $input) {
      accountId
      organisations {
        id
        permissions {
          section
          value
        }
        sectionVisibility {
          section
          visibility
        }
        resolvedPermissions {
          section
          value
        }
        resolvedSectionVisibility {
          section
          visibility
        }
      }
    }
  }
`;
